/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export interface RequestEntry {
  id: number;
  time: string;
  service: string;
  host: string;
  method: string;
  path: string;
  status: number;
  clientIp: string;
  /** reverse-DNS name of an external client address, when it has one */
  clientHost?: string;
  durationMs: number;
  userAgent?: string;
  probe: boolean;
  /** traefik | ssh | fail2ban | pveproxy | pve-firewall | conn */
  source: string;
  /** http | auth | ban | firewall | conn */
  kind: string;
  /** which machine logged it: vps, or an agent name such as homelab */
  origin: string;
  /** local | vpn | external, from the client address */
  network: "local" | "vpn" | "external";
  scheme?: string;
  protocol?: string;
  entryPoint?: string;
  router?: string;
  upstream?: string;
  tls?: string;
  user?: string;
  message?: string;
  port?: number;
  bytesOut?: number;
  bytesIn?: number;
}

/** What to show for a client: its domain when known, otherwise the address. */
export const clientLabel = (r: Pick<RequestEntry, "clientIp" | "clientHost">): string => r.clientHost || r.clientIp;

export interface RecentRequests {
  enabled: boolean;
  entries: RequestEntry[];
  lastId: number;
}

/** Fetch proxied requests newer than `since` (0 = latest batch). */
export async function getRecentRequests(
  since = 0,
  limit = 50
): Promise<RecentRequests> {
  const res = await fetch(
    getApiUrl(`/requests/recent?since=${since}&limit=${limit}`),
    { credentials: "include" }
  );
  if (!res.ok) throw new Error("Failed to load requests");
  return (await res.json()) as RecentRequests;
}

export interface SecurityIP {
  ip: string;
  host?: string;
  org?: string;
  count: number;
  kinds: string[];
  lastSeen: string;
  country?: string;
  banned: boolean;
}

export interface SecuritySummary {
  enabled: boolean;
  canBan: boolean;
  failedLogins?: number;
  bans?: number;
  drops?: number;
  probes?: number;
  unique?: number;
  hourly?: number[];
  top?: SecurityIP[];
}

export async function getSecuritySummary(): Promise<SecuritySummary> {
  const res = await fetch(getApiUrl("/requests/security"), { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load security summary");
  return (await res.json()) as SecuritySummary;
}

export async function banIp(ip: string): Promise<void> {
  const res = await fetch(getApiUrl("/requests/ban"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ip }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? "Failed to ban");
}

export interface IPInfo {
  ip: string;
  host?: string;
  country?: string;
  city?: string;
  asn?: number;
  org?: string;
  prefix?: string;
  public: boolean;
  found: boolean;
}

/** Who owns an address and where it is (reverse DNS, organisation, AS, network, country). */
export async function getIpInfo(ip: string): Promise<IPInfo> {
  const res = await fetch(getApiUrl(`/requests/ip/${encodeURIComponent(ip)}`), { credentials: "include" });
  if (!res.ok) throw new Error("Failed to look the address up");
  return (await res.json()) as IPInfo;
}
